
import Vue from 'vue';
import { VideoItem } from '@media/types/getVideo';
import getRootConfig from '../../utils/getRootConfig';

declare let window: Window;

interface Props {
  data: {
    attrs?: VideoItem;
    externalSource?: boolean;
    html?: string;
    type?: string;
  };
  type: string;
  ads: {
    adFree: boolean;
    keywords: string[];
  };
}

interface Data {
  code: any | null;
  className: string;
  liveAttrs: Record<string, any> | null;
}

interface Computed {
  id: string | null;
  provider: string | null;
}

export default Vue.extend<unknown, Data, Computed, Props>({
  props: {
    type: {
      type: String,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
    ads: {
      type: Object,
      required: false,
      default: () => ({
        adFree: false,
        keywords: [],
      }),
    },
  },
  data() {
    return {
      code: null,
      className: 'content',
      liveAttrs: null,
    };
  },
  computed: {
    id() {
      if (this.code && this.code.videoCode) {
        const codeSplit = this.code.videoCode.split(':');
        return codeSplit[1];
      }

      return null;
    },
    provider() {
      if (this.code && this.code.videoCode) {
        const codeSplit = this.code.videoCode.split(':');
        return codeSplit[0];
      }

      return null;
    },
  },
  async mounted() {
    if (this.data.attrs?.lead) {
      this.className = 'lead';
    }

    // Detect live streams (video channel)
    if (this.data.attrs?.streamElement) {
      const streamElement = this.data.attrs?.streamElement;
      this.code = { videoCode: streamElement.videoCode };
    }

    // Detect DVideo embed script and extract DVideo id
    const pattern = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
    if (this.data.html && this.data.html.match(pattern)) {
      const matches = this.data.html.match(pattern);
      if (matches && matches.length > 0) {
        const script = matches[0];
        const src = script.substring(script.indexOf('src=') + 5, script.indexOf('"', script.indexOf('src=') + 5));
        const id = src.substring(src.indexOf('#') + 1).replace('id=', '');
        this.code = { videoCode: `dvideo:${id}` };
      }
    }

    // Detect EmbeddedArticleVideo content and get data remotely
    const pattern_eav = /embedded_article_video:\d+/gi;
    if (this.data.html && this.data.html.match(pattern_eav)) {
      const matches_eav = this.data.html.match(pattern_eav);
      if (matches_eav && matches_eav.length > 0) {
        const videoCode = matches_eav[0];
        const response = await fetch(`//www.delfi.lt/misc/pc/get_video_embed_code.php?videoCode=${videoCode}`);
        if (response.ok) {
          const data = await response.json();
          if (data.videoCode) {
            this.code = { videoCode: data.videoCode };
          }
        }
      }
    }

    // Detect live_embed
    if (this.data.type === 'live_embed') {
      this.code = { videoCode: `live_embed:${this.data.attrs?.id}` };
      this.liveAttrs = {};
      this.liveAttrs.hls = this.data.attrs?.hls;
      this.liveAttrs.poster = this.data.attrs?.poster;
      this.liveAttrs.title = this.data.attrs?.title;
    }

    if (process.client) {
      this.$nextTick(() => {
        const script = document.createElement('script');
        script.type = 'text/javascript';

        if (this.provider === 'youtube') {
          script.src = 'https://g.delfi.lt/glt/c/delfi-live/yt.js';
          script.dataset.video = `${this.id}`;
          script.dataset.width = '100%';
          script.dataset.autoplay = '1';
        } else if (this.provider === 'live_embed') {
          script.src = 'https://g.delfi.lt/glt/c/delfi-live/embed.js';
          script.dataset.hls = `${this.liveAttrs?.hls}`;
          script.dataset.poster = `${this.liveAttrs?.poster}`;
          script.dataset.title = `${this.liveAttrs?.title}`;
          script.dataset.autoplay = '1';

          if (this.liveAttrs?.logo) {
            script.dataset.logo = this.liveAttrs?.logo;
          }

          if (this.liveAttrs?.logoimg) {
            script.dataset.logoimg = this.liveAttrs?.logoimg;
          }

          if (this.liveAttrs?.logourl) {
            script.dataset.logourl = this.liveAttrs?.logourl;
          }

          if (this.liveAttrs?.gsga) {
            script.dataset.gsga = this.liveAttrs?.gsga;
          }
        } else {
          script.src = 'https://g.delfi.lt/components/video/player.js';
          script.dataset.provider = 'dvideo';
          script.dataset.id = `${this.id}`;
          script.dataset.autoplay = '1';

          if (typeof window !== 'undefined' && window.$nuxt && window.$nuxt.$route.params.category) {
            const categorySlug = window.$nuxt.$route.params.subcategory || window.$nuxt.$route.params.category;
            const customAdFormSettings = getRootConfig('page')?.article?.component?.adForm;
            if (customAdFormSettings) {
              if (Array.isArray(customAdFormSettings)) {
                const categoryAdsConfig = customAdFormSettings.find((item: { categories: string | string[] }) => item.categories.includes(categorySlug)) || [];

                if (categoryAdsConfig.length > 0 && categoryAdsConfig?.disableAds.includes('videoAds')) {
                  script.dataset.noads = 'true';
                }
              }
            }
          }
        }

        window.document.getElementsByClassName(`${this.provider}-${this.className}-container-${this.id}`)[0]!.append(script);
      });
    }
  },
});
